<template>
    <div class="b2b-card">
        <div class="header">
            <h2 class="title-l">Billing & Payment</h2>
            <Link v-if="allowEdit" :href="route('checkout.billing')" class="text-rothcoWhite">Edit</Link>
        </div>
        <div class="content">

            <div v-if="!d2c" class="billing row">
                <div class="title-s title">
                    P.O. Number
                </div>
                <form v-if="allowEdit && editingPoNumber && !d2c" class="billing-edit-form pb-75" @submit.prevent="editPoNumber(); updateBillingField('billing_po_number')" id="billing_form_po_number" name="billing_form_po_number">
                    <div class="input-folder input-folder--input input-masked">
                        <input v-model="form.billing_po_number" type="text" id="billing_po_number" name="billing_po_number" placeholder=" " class="b2b-billing-input-label">
                        <label for="billing_po_number">
                            <p class="paragraph-default">P.O. Number</p>
                        </label>
                    </div>
                    <button :disabled="form.processing" type="submit" class="primary">Save</button>
                </form>
                <div v-else-if="!d2c" class="paragraph-default">
                    {{ form.billing_po_number }} 
                    <a v-if="allowEdit" href="#" @click.prevent="editPoNumber()" class="text-rothcoRed">{{ form.billing_po_number ? 'Edit' : 'Add' }}</a>
                </div>
            </div>
            <div class="billing row">
                <div class="title-s title">
                    Email
                </div>
                <div class="pb-25 paragraph-default">
                    {{ billing.billing_email }}
                </div>
            </div>
            <div v-if="!d2c" class="billing row">
                <div class="title-s title">
                    Alternative Email
                </div>
                <div class="pb-25 paragraph-default">
                    {{ billing.billing_alternate_email }}
                </div>
            </div>
            <div v-if="!d2c" class="billing row">
                <div class="title-s title">
                    Order Type
                </div>
                <div class="pb-25 paragraph-default">
                    {{ billing.order_type }}
                </div>
            </div>
            <div v-if="!d2c" class="billing row">
                <div class="title-s title">
                    Billing Comments
                </div>
                <div class="pb-25 paragraph-default billing-comments" style="display: block">
                    <form v-if="allowEdit && editingBillingComment && !d2c" @submit="editBillingComment(); updateBillingField('billing_comments')" name="billing_form_comments">
                        <div class="textarea-folder">
                            <PrimeTextArea 
                                v-model="form.billing_comments"
                                id="billing_comments"
                                name="billing_comments"
                                placeholder=" " 
                                class="input-folder--input">
                            </PrimeTextArea>
                            <label class="paragraph-default" for="billing_comments">Billing Comments</label>
                        </div>
                        <button :disabled="form.processing" type="submit" class="primary">Save</button>
                    </form>
                    <span v-else-if="!d2c">
                        {{ form.billing_comments }} <a v-if="allowEdit" href="#" @click.prevent="editBillingComment()" class="text-rothcoRed">{{ form.billing_comments ? 'Edit' : 'Add' }}</a>
                    </span>
                </div>
            </div>

            <div class="payment row">
                <div class="title-s title">Payment</div>
                <div v-if="billing.billing_payment_method === 'MV'" class="payment-content text-rothcoText">
                    <div class="payment-icon">
                        <PaymentIcon alt="" class="" aria-label="hidden" role="presentation"/>
                    </div>
                    <div>
                        <div class="pb-25 paragraph-default">XXXX-XXXX-XXXX-{{ billing?.creditCard.cc_last_4 }}</div>
                        <div class="pb-25 paragraph-default">Expiration: {{ billing?.creditCard.cc_exp_month }}/{{ billing.creditCard.cc_exp_year.slice(-2) }}</div>
                        <div v-if="billing?.creditCard?.cc_name" class="pb-25 paragraph-default">Name on Card: {{ billing?.creditCard.cc_name }}</div>
                    </div>
                </div>
                <div v-else class="payment-content text-rothcoText paragraph-default">
                    {{ paymentMethod }}
                </div>
            </div>
            <div v-if="billing.billing_campaign_code" class="billing row">
                <div class="title-s title">Campaign Code</div>
                <div class="payment-content text-rothcoText paragraph-default">
                    {{ billing.billing_campaign_code }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, inject } from "vue";
    import { Link } from "@inertiajs/vue3";
    // import { route } from "ziggy-js";
    import { useForm } from "@/helpers/composables/useForm";
    import { string, object } from "yup";
    import PaymentIcon from "@icons/payment-icon.svg";
    let route = inject("route");
    const props = defineProps({
        paymentMethods: {
            type: Object,
            default: () => ({
                code: "MV", name: "Credit Card"
            })
        },
        allowEdit: {
            type: Boolean,
            default: false
        },
        d2c: {
            type: Boolean,
            default: false
        }
    });

    const billing = defineModel({
        type: Object,
        default() {
            return {
                billing_po_number: "",
                billing_email: "",
                billing_alternate_email: "",
                order_type: "",
                billing_payment_method: "",
                billing_comments: "",
                billing_campaign_code: "",
                creditCard: {
                    cc_name: "",
                    cc_exp_month: "",
                    cc_exp_year: "",
                    cc_last_4: ""
                }
            };
        }
    });

    const paymentMethod = computed(() => {
        let currentPaymentMethod = props.paymentMethods?.find((method)=>(method.code == billing.value.billing_payment_method));
        return (currentPaymentMethod?.name || billing.value.billing_payment_method);
    });

    let editingPoNumber = ref(false);
    const editPoNumber = () => {
        editingPoNumber.value = !editingPoNumber.value;
    };

    let editingBillingComment = ref(false);
    const editBillingComment = () => {
        editingBillingComment.value = !editingBillingComment.value;
    };

    const form = useForm({
        useInertia: true,
        inertiaAlias: "DealerConfirm",
        schema: object({
            // BILLING
            billing_po_number: string().nullable().notRequired().default(billing.value.billing_po_number),
            billing_comments: string().nullable().notRequired().default(billing.value.billing_comments),
        })
    })

    const updateBillingField = (field) => {
        if(form.processing) return;
        if (!form.hasErrors) {
            form.transform((data) => {
                return {
                    [field]: data[field]
                }
            }).post(route("checkout.save-review"), {preserveScroll: true, preserveState: true});

            billing.value[field] = form[field];
        }
    };

    const editingFields = computed(()=>{
        if(!props.allowEdit || form.hasErrors) return {};
        // used to prevent edited + unsaved fields from being lost when placing an order 
        let fields = {};
        if(editingPoNumber.value){ // is editing po number
            fields['billing_po_number'] = form.billing_po_number;
        }
        if(editingBillingComment.value){ // is editing billing comments
            fields['billing_comments'] = form.billing_comments;
        }
        return fields;
    });

    defineExpose({editingFields})
</script>
